import React from 'react';
import { Card } from '../common/Card';
import {
    CreditCard, Bell, BarChart, Calendar, DollarSign, Shield,
    Smartphone, RefreshCcw, PieChart, Tags, Users, FileText,
    Mail, Clock, Wallet, Settings
} from 'lucide-react';

const features = [
    {
        category: "Core Features",
        items: [
            {
                icon: CreditCard,
                title: "Track All Subscriptions",
                description: "Easily add and monitor all your subscriptions in one dashboard."
            },
            {
                icon: Bell,
                title: "Smart Reminders",
                description: "Get notified before payments are due to avoid late fees and service interruptions.",
                status: "beta"
            },
            {
                icon: BarChart,
                title: "Spending Analytics",
                description: "Visualize your subscription spending patterns with detailed charts and graphs."
            }
        ]
    },
    {
        category: "Money Management",
        items: [
            {
                icon: DollarSign,
                title: "Budget Planning",
                description: "Set monthly budgets and get alerts when you're approaching your limit.",
                status: "beta"
            },
            {
                icon: Wallet,
                title: "Cost Optimization",
                description: "Receive suggestions for optimizing your subscription costs and finding better deals.",
                status: "coming soon"
            },
            {
                icon: PieChart,
                title: "Expense Categories",
                description: "Organize subscriptions by category to better understand your spending.",
                status: "coming soon"
            }
        ]
    },
    {
        category: "Smart Tools",
        items: [
            {
                icon: Calendar,
                title: "Renewal Tracking",
                description: "Keep track of renewal dates and never miss a payment or deadline.",
                status: "beta"
            },
            // {
            //     icon: RefreshCcw,
            //     title: "Auto-Sync",
            //     description: "Automatically sync with your bank accounts to track subscription charges."
            // },
            {
                icon: Tags,
                title: "Smart Categorization",
                description: "AI-powered categorization of your subscriptions for better organization.",
                status: "coming soon"
            }
        ]
    },
    // {
    //     category: "Team & Sharing",
    //     items: [
    //         {
    //             icon: Users,
    //             title: "Family Sharing",
    //             description: "Share subscription management with family members or team members.",
    //             status: "coming soon"
    //         },
    //         {
    //             icon: FileText,
    //             title: "Reports & Export",
    //             description: "Generate detailed reports and export data for accounting purposes.",
    //             status: "coming soon"
    //         },
    //         {
    //             icon: Mail,
    //             title: "Email Digests",
    //             description: "Receive weekly or monthly summaries of your subscription activity.",
    //             status: "coming soon"
    //         }
    //     ]
    // },
    {
        category: "Advanced Features",
        items: [
            {
                icon: Shield,
                title: "Secure Vault",
                description: "Safely store subscription credentials with bank-level encryption.",
                status: "coming soon"
            },
            {
                icon: Smartphone,
                title: "Mobile Access",
                description: "Manage your subscriptions on the go with our mobile app.",
                status: "coming soon"
            },
            {
                icon: Settings,
                title: "Custom Rules",
                description: "Create custom rules and automations for subscription management.",
                status: "coming soon"
            }
        ]
    },
    // {
    //     category: "Integration & Connectivity",
    //     items: [
    //         {
    //             icon: RefreshCcw,
    //             title: "Bank Integration",
    //             description: "Connect with your bank accounts for automatic transaction tracking."
    //         },
    //         {
    //             icon: Clock,
    //             title: "Real-time Updates",
    //             description: "Get instant updates on subscription changes and payment processing."
    //         },
    //         {
    //             icon: Shield,
    //             title: "API Access",
    //             description: "Access our API for custom integrations and automation workflows."
    //         }
    //     ]
    // },
    // {
    //     category: "Support & Resources",
    //     items: [
    //         {
    //             icon: FileText,
    //             title: "Knowledge Base",
    //             description: "Access comprehensive guides and tutorials for better subscription management."
    //         },
    //         {
    //             icon: Users,
    //             title: "24/7 Support",
    //             description: "Get help anytime with our dedicated customer support team."
    //         },
    //         {
    //             icon: Mail,
    //             title: "Community Forum",
    //             description: "Connect with other users and share tips for subscription management."
    //         }
    //     ]
    // }
];

const StatusBadge = ({ status }) => {
    if (!status) return null;

    const styles = {
        'coming soon': 'bg-purple-100 text-purple-800',
        'beta': 'bg-green-100 text-green-800',
        'new': 'bg-blue-100 text-blue-800'
    };

    return (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[status] || ''}`}>
            {status}
        </span>
    );
};

export const DetailedFeatures = () => {
    return (
        <section className="py-20 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-16">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        All Features
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Discover all the powerful features that make KeepItSorted the best choice for managing your subscriptions
                    </p>
                </div>

                {features.map((category, categoryIndex) => (
                    <div key={categoryIndex} className="mb-16 last:mb-0">
                        <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
                            {category.category}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {category.items.map((feature, index) => (
                                <Card
                                    key={index}
                                    className="p-6 hover:shadow-lg transition-shadow duration-300 ease-in-out"
                                >
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center">
                                            <div className="p-2 bg-blue-50 rounded-lg">
                                                <feature.icon className="h-6 w-6 text-blue-500" />
                                            </div>
                                            <h4 className="text-xl font-semibold ml-3">{feature.title}</h4>
                                        </div>
                                        <StatusBadge status={feature.status} />
                                    </div>
                                    <p className="text-gray-600">{feature.description}</p>
                                </Card>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
}; 